import { Text, TextChip, Tooltip } from '@shared/components';
import { AssigneeOptionVariant } from '@shared/components/dropdown-chip/DropdownChip';
import { useActionExecutionCreditsQuery } from '@shared/generated/graphql';
import { StyleProps } from '@shared/utils';
import clsx from 'clsx';
import { ReactElement } from 'react';
import {
  CreditsGroup,
  formatCredits,
  groupCreditsBySop,
} from './creditsBreakdown';

const MAX_SHOWN_GROUPS = 8;
// NOTE(jacob): Usage is billed asynchronously after the work happens, so an
// open task keeps refreshing, far slower than the chat's own polling.
const CREDITS_POLL_INTERVAL_MS = 60 * 1000;

type ActionExecutionCreditsBadgeProps = StyleProps & {
  actionExecutionId: string;
  /** What the breakdown calls the execution's own usage. */
  ownLabel?: string;
};

/**
 * Renders nothing for viewers who can't see their organization's usage, or
 * when nothing has been billed yet.
 */
export function ActionExecutionCreditsBadge({
  actionExecutionId,
  className,
  ownLabel = 'This task',
}: ActionExecutionCreditsBadgeProps): ReactElement | null {
  const { data } = useActionExecutionCreditsQuery({
    variables: { id: actionExecutionId },
    fetchPolicy: 'cache-and-network',
    pollInterval: CREDITS_POLL_INTERVAL_MS,
  });
  const credits = data?.actionExecutionCredits;
  if (!credits || credits.totalCredits === 0) return null;

  return (
    <Tooltip
      isInstant
      side="bottom"
      contentClassName="min-w-[220px] px-2 py-1 text-left"
      content={
        <CreditsBreakdown
          ownLabel={ownLabel}
          ownCredits={credits.ownCredits}
          downstreamCredits={credits.downstreamCredits}
          groups={groupCreditsBySop(credits.downstream)}
        />
      }
    >
      <div
        className={clsx(
          'focus-visible:outline-brand-500 rounded-2xl focus-visible:outline focus-visible:outline-2',
          className
        )}
        aria-label="Credits billed"
        tabIndex={0}
      >
        <TextChip
          text={`${formatCredits(credits.totalCredits)} credits`}
          shouldCapitalize={false}
          variant={AssigneeOptionVariant.DEFAULT}
        />
      </div>
    </Tooltip>
  );
}

function CreditsBreakdown({
  ownLabel,
  ownCredits,
  downstreamCredits,
  groups,
}: {
  ownLabel: string;
  ownCredits: number;
  downstreamCredits: number;
  groups: CreditsGroup[];
}) {
  const shownGroups = groups.slice(0, MAX_SHOWN_GROUPS);
  const hiddenGroups = groups.slice(MAX_SHOWN_GROUPS);
  const hiddenCredits = hiddenGroups.reduce((sum, g) => sum + g.credits, 0);

  return (
    <div className="flex flex-col gap-1">
      <CreditsRow label={ownLabel} credits={ownCredits} isHeavy />
      <CreditsRow label="Downstream" credits={downstreamCredits} isHeavy />
      {shownGroups.map((group) => (
        <CreditsRow
          key={group.actionDefinitionId}
          label={
            group.executionCount > 1
              ? `${group.actionDefinitionName} ×${group.executionCount}`
              : group.actionDefinitionName
          }
          credits={group.credits}
          isIndented
        />
      ))}
      {hiddenGroups.length > 0 && (
        <CreditsRow
          label={`${hiddenGroups.length} more`}
          credits={hiddenCredits}
          isIndented
        />
      )}
    </div>
  );
}

function CreditsRow({
  label,
  credits,
  isHeavy = false,
  isIndented = false,
}: {
  label: string;
  credits: number;
  isHeavy?: boolean;
  isIndented?: boolean;
}) {
  return (
    <div className={clsx('flex justify-between gap-4', isIndented && 'pl-3')}>
      <Text type="body-xs" isHeavy={isHeavy} className="truncate">
        {label}
      </Text>
      <Text type="body-xs" isHeavy={isHeavy} className="shrink-0">
        {formatCredits(credits)}
      </Text>
    </div>
  );
}
